import type { SchemaProposal } from '@/services/api/schemaSessions'
import type { MergeStrategy, PropertySchema } from '@/services/api/schemas'

export type ProposalSection = 'entities' | 'relations' | 'patterns'
export interface ProposalElement {
  key: string
  label: string
  section: ProposalSection
  description: string
  properties: PropertySchema[]
  /** Relation endpoints (`source_types` / `target_types`) or pattern legs. */
  sourceTypes: string[]
  targetTypes: string[]
  trackable: boolean
  mergeStrategy: MergeStrategy | null
  definition: Record<string, unknown>
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

function property(value: unknown): PropertySchema | null {
  const raw = record(value)
  if (typeof raw.name !== 'string') return null
  return {
    name: raw.name,
    type: typeof raw.type === 'string' ? raw.type : 'string',
    description: typeof raw.description === 'string' ? raw.description : undefined,
    required: raw.required === true,
    unique: raw.unique === true,
    is_display_name: raw.is_display_name === true,
  }
}

function mergeStrategy(value: unknown): MergeStrategy | null {
  return value === 'any' || value === 'all' ? value : null
}

function element(section: ProposalSection, index: number, value: unknown): ProposalElement {
  const definition = Array.isArray(value) ? { pattern: value } : record(value)
  const pattern = Array.isArray(value)
    ? value
    : [definition.source_type, definition.relationship_type, definition.target_type]
  const isPattern = section === 'patterns'
  const [patternSource, patternRel, patternTarget] = pattern.map(part => String(part ?? '?'))
  return {
    key: `${section}:${index}`,
    section,
    label: isPattern
      ? `${patternSource} → ${patternRel} → ${patternTarget}`
      : String(definition.label ?? '?'),
    description: typeof definition.description === 'string' ? definition.description : '',
    properties: (Array.isArray(definition.properties) ? definition.properties : [])
      .map(property)
      .filter((p): p is PropertySchema => p !== null),
    sourceTypes: isPattern ? [patternSource] : strings(definition.source_types),
    targetTypes: isPattern ? [patternTarget] : strings(definition.target_types),
    trackable: definition.trackable === true,
    mergeStrategy: mergeStrategy(definition.merge_strategy),
    definition,
  }
}

/** Failed proposals have no persisted IDs and can use either portable pattern shape. */
export function proposalElements(proposal: SchemaProposal): ProposalElement[] {
  return (
    [
      ['entities', 'entity_types'],
      ['relations', 'relationship_types'],
      ['patterns', 'patterns'],
    ] as const
  ).flatMap(([section, key]) => {
    const values = proposal.schema[key]
    if (!Array.isArray(values)) return []
    return values.map((value, index) => element(section, index, value))
  })
}
